import { useEffect, useState } from 'react';
import { useSidebar } from '@/context/sidebar';
import { useAuth } from '@/store/useAppStore';
import { isPublicLinkSession } from '@/lib/supabase';
import { useProfileIdentity } from '@/hooks/useProfileIdentity';
import { NotificationBell } from '@/features/notifications';

/**
 * Header de l'application
 * Design: Minimalist White pour contraster avec la Dark Sidebar
 */
export function Header() {
  const { isMobileOpen, setIsMobileOpen, isExpanded, toggleSidebar } = useSidebar();
  const { profile, signOut } = useAuth();
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : false
  );

  // Une visite par lien public n'a pas de ligne dans `profiles` :
  // on affiche le nom de l'entreprise à la place (voir plus bas).
  const { data: userProfile } = useProfileIdentity(profile?.id, !isPublicLinkSession);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleToggle = () => {
    if (isDesktop) {
      toggleSidebar();
    } else {
      setIsMobileOpen(!isMobileOpen);
    }
  };

  const formatRole = (role?: string | null) => {
    if (!role) return '';
    return role
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  };

  const formattedRole = formatRole(userProfile?.role);
  const userFullName =
    userProfile && (userProfile.first_name || userProfile.last_name)
      ? `${userProfile.first_name ?? ''} ${userProfile.last_name ?? ''}`.trim()
      : '';

  return (
    <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-slate-200 z-header h-20 flex flex-col justify-center transition-all duration-300">
      <div className="flex items-center justify-between px-6 w-full">
        {/* Gauche : Hamburger & Titre Mobile */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleToggle}
            className="flex items-center justify-center w-10 h-10 text-slate-500 rounded-xl hover:bg-slate-100 hover:text-slate-900 transition-all focus:outline-none focus:ring-2 focus:ring-slate-200"
            aria-label="Menu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12h18M3 6h18M3 18h18"/>
            </svg>
          </button>

          {/* Titre affiché uniquement si sidebar fermée ou mobile */}
          {(!isDesktop || !isExpanded) && (
            <div className="flex flex-col animate-in fade-in duration-300">
              <span className="font-display text-xl font-bold text-slate-900">Astéroïde</span>
              <span className="text-[10px] font-medium uppercase tracking-[0.3em] text-slate-400">Production</span>
            </div>
          )}
        </div>

        {/* Droite : Actions & Profil */}
        <div className="flex items-center gap-2 md:gap-6">
          {/* Les notifications sont rattachées à un compte : sans objet par lien public */}
          {!isPublicLinkSession && (
            <>
              <div className="border-r border-slate-200 pr-6 hidden md:block">
                <NotificationBell />
              </div>

              {/* Mobile Actions */}
              <div className="md:hidden flex items-center gap-2">
                <NotificationBell />
              </div>
            </>
          )}

          {/* Visite par lien public : nom de l'entreprise, ni compte ni déconnexion */}
          {isPublicLinkSession ? (
            <div className="hidden md:flex items-center gap-4">
              <div className="flex flex-col items-end">
                <span className="text-sm font-semibold text-slate-900">{profile?.first_name || 'Espace client'}</span>
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider bg-slate-100 px-2 py-0.5 rounded-full">
                  Espace client
                </span>
              </div>
              <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm shadow-lg shadow-slate-200 ring-2 ring-white">
                {(profile?.first_name || 'C').charAt(0).toUpperCase()}
              </div>
            </div>
          ) : (
          <div className="hidden md:flex items-center gap-4 group cursor-pointer relative">
             <div className="flex flex-col items-end">
                <span className="text-sm font-semibold text-slate-900">{userFullName || 'Utilisateur'}</span>
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider bg-slate-100 px-2 py-0.5 rounded-full">
                  {formattedRole || 'Membre'}
                </span>
             </div>
             
             <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm shadow-lg shadow-slate-200 ring-2 ring-white">
                {userProfile?.first_name?.[0]}{userProfile?.last_name?.[0]}
             </div>

             {/* Dropdown Déconnexion simple au survol/clic */}
             <div className="absolute top-full right-0 mt-2 w-48 py-2 bg-white rounded-xl shadow-xl border border-slate-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0">
                <div className="px-4 py-2 border-b border-slate-50 mb-1">
                   <p className="text-xs text-slate-400">Connecté en tant que</p>
                   <p className="text-sm font-semibold truncate">{userFullName}</p>
                </div>
                <button 
                  onClick={signOut}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                  Déconnexion
                </button>
             </div>
          </div>
          )}

          {/* Mobile Logout */}
          {!isPublicLinkSession && (
          <button
            onClick={signOut}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-xl bg-slate-100 text-slate-600 hover:bg-red-50 hover:text-red-500 transition-all"
            aria-label="Déconnexion"
          >
             <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          </button>
          )}
        </div>
      </div>
    </header>
  );
}
